import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

type UseDeltaTSliderParams = {
  value?: number;
  onChange: (value?: number) => void;
  debounceMs?: number;
};

export const useDeltaTSlider = ({ value, onChange, debounceMs = 300 }: UseDeltaTSliderParams) => {
  const [draftValue, setDraftValue] = useState<number | undefined>(value);
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 1,
        unit: 'celsius',
        unitDisplay: 'narrow',
        style: 'unit',
      }),
    []
  );
  const debouncedChange = useDebouncedCallback(
    (nextValue?: number) => onChange(nextValue),
    debounceMs
  );

  const getPosition = useCallback((sliderValue: number, min: number, max: number) => {
    if (max <= min) {
      return 0;
    }

    return Math.min(100, Math.max(0, ((sliderValue - min) / (max - min)) * 100));
  }, []);
  const formatValue = useCallback((nextValue: number) => formatter.format(nextValue), [formatter]);

  const handleChange = useCallback(
    (nextValue: number) => {
      setDraftValue(nextValue);
      debouncedChange(nextValue);
    },
    [debouncedChange]
  );

  const clear = useCallback(() => {
    debouncedChange.cancel();
    setDraftValue(undefined);
    onChange(undefined);
  }, [debouncedChange, onChange]);

  const clearDraft = useCallback(() => {
    debouncedChange.cancel();
    setDraftValue(undefined);
  }, [debouncedChange]);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  return {
    clear,
    clearDraft,
    draftValue,
    formatValue,
    getPosition,
    handleChange,
  };
};
